import { useMemo, useState } from 'react';
import type { TFunction } from 'i18next';
import {
  BLOCKED_DETAILS_SUBMIT,
  COMPLETE_DETAIL_FIELD,
  SELECT_DETAIL_FIELD,
} from '@/consts/events';
import { emailIsValid } from '@/utils/email';
import { MBI_LENGTH, mbiIsValid, normalizeMbi } from '@/utils/mbi';
import {
  FIRST_NAME_MAX_LENGTH,
  LAST_NAME_MAX_LENGTH,
  nameIsTooLong,
  nameIsValid,
} from '@/utils/name';
import type { WregStep } from '../wregSteps';
import {
  FIELD_NAME,
  INTAKE_ABOUT_FIELDS,
  INTAKE_CONTACT_FIELDS,
  INTAKE_MEDICARE_FIELDS,
  INTAKE_NAME_FIELDS,
  INTAKE_PROMO_FIELDS,
  dobIsImpossible,
  dobIsOutOfRange,
  intakeEventProperties,
  intakeFieldNames,
  type IntakeErrorKey,
  type WregIntakeForm,
} from './wregIntakeFields';
import type { WregInvalidityReason, WregScreenEvent } from './wregScreenEvents';

type IntakeErrors = Partial<Record<IntakeErrorKey, string>>;

const REQUIRED_FIELD_MESSAGE = 'This field is required.';
const INVALID_DOB_MESSAGE = 'Enter a complete date of birth.';
const INVALID_MBI_MESSAGE = 'Enter all 11 characters from your Medicare card.';
const INVALID_MBI_FORMAT_MESSAGE =
  'Check the number against your card. Medicare numbers never use the letters B, I, L, O, S or Z.';
const INVALID_NAME_MESSAGE =
  'Use letters, spaces, hyphens, apostrophes and periods only, as shown on your Medicare card.';
const nameTooLongMessage = (maxLength: number) => `Use ${maxLength} characters or fewer.`;
const INVALID_EMAIL_MESSAGE = 'Enter a valid email address.';
const INVALID_PHONE_MESSAGE = 'Enter a 10-digit phone number.';

const nameError = (name: string, maxLength: number): string | null => {
  if (!name.trim()) return REQUIRED_FIELD_MESSAGE;
  if (nameIsTooLong(name, maxLength)) return nameTooLongMessage(maxLength);
  return nameIsValid(name, maxLength) ? null : INVALID_NAME_MESSAGE;
};

const errorId = (field: IntakeErrorKey) => `wreg-int-${field}-err`;

const controlId = (field: IntakeErrorKey) => `wreg-int-${field}`;

type Options = {
  fields: IntakeErrorKey[];
  form: WregIntakeForm;
  valid: boolean;
  step: WregStep;
  t: TFunction;
  onEvent?: WregScreenEvent;
};

const useIntakeErrors = ({ fields, form: f, valid, step, t, onEvent }: Options) => {
  const [touched, setTouched] = useState<Set<IntakeErrorKey>>(() => new Set());
  const [submitAttempted, setSubmitAttempted] = useState(false);

  const dobOutOfRange = dobIsOutOfRange(f.dob);
  const dobUnreal = dobIsImpossible(f.dob);

  const errors = useMemo<IntakeErrors>(() => {
    const next: IntakeErrors = {};
    const first = nameError(f.first, FIRST_NAME_MAX_LENGTH);
    if (first) next.first = first;
    const last = nameError(f.last, LAST_NAME_MAX_LENGTH);
    if (last) next.last = last;
    if (dobOutOfRange || dobUnreal) next.dob = t('wreg.intake.dobnotice');
    else if (f.dob.replace(/\D/g, '').length !== 8) next.dob = INVALID_DOB_MESSAGE;
    if (!f.gender) next.gender = REQUIRED_FIELD_MESSAGE;
    if (normalizeMbi(f.mbi).length !== MBI_LENGTH) next.mbi = INVALID_MBI_MESSAGE;
    else if (!mbiIsValid(f.mbi)) next.mbi = INVALID_MBI_FORMAT_MESSAGE;
    if (!f.promo.trim()) next.promo = REQUIRED_FIELD_MESSAGE;
    if (!f.email.trim()) next.email = REQUIRED_FIELD_MESSAGE;
    else if (!emailIsValid(f.email)) {
      next.email = INVALID_EMAIL_MESSAGE;
    }
    if (f.phone.length !== 10) next.phone = INVALID_PHONE_MESSAGE;
    return next;
  }, [dobOutOfRange, dobUnreal, f, t]);

  const touch = (field: IntakeErrorKey) => () =>
    setTouched((previous) => new Set(previous).add(field));

  const focus = (field: IntakeErrorKey) => () =>
    onEvent?.(SELECT_DETAIL_FIELD, { field: FIELD_NAME[field] });

  const invalidityReason = (field: IntakeErrorKey): WregInvalidityReason =>
    field === 'dob' && (dobOutOfRange || dobUnreal) ? 'out_of_range' : 'format';

  const blur = (field: IntakeErrorKey) => () => {
    touch(field)();
    if (!String(f[field]).trim()) return;
    const isValid = !errors[field];
    onEvent?.(COMPLETE_DETAIL_FIELD, {
      field: FIELD_NAME[field],
      is_valid: isValid,
      ...(isValid ? {} : { invalidity_reason: invalidityReason(field) }),
    });
  };

  const mbiIsFullAndMalformed = normalizeMbi(f.mbi).length === MBI_LENGTH && !mbiIsValid(f.mbi);

  const showError = (field: IntakeErrorKey) =>
    fields.includes(field) &&
    Boolean(errors[field]) &&
    (submitAttempted ||
      touched.has(field) ||
      (field === 'dob' && (dobOutOfRange || dobUnreal)) ||
      (field === 'mbi' && mbiIsFullAndMalformed));

  const validateBeforeSubmit = () => {
    if (valid) return true;

    setSubmitAttempted(true);
    onEvent?.(BLOCKED_DETAILS_SUBMIT, {
      ...intakeEventProperties(step),
      invalid_fields: fields.filter((field) => errors[field]).map((field) => FIELD_NAME[field]),
    });
    const firstInvalid = fields.find((field) => errors[field]);
    if (firstInvalid) {
      requestAnimationFrame(() => {
        const control = document.getElementById(controlId(firstInvalid));
        const target = control?.querySelector<HTMLElement>('[role="radio"]') ?? control;
        target?.focus();
        control?.scrollIntoView?.({ block: 'center', behavior: 'smooth' });
      });
    }
    return false;
  };

  return {
    errors,
    touch,
    focus,
    blur,
    showError,
    errorId,
    validateBeforeSubmit,
  };
};

export {
  useIntakeErrors,
  INTAKE_NAME_FIELDS,
  INTAKE_CONTACT_FIELDS,
  INTAKE_ABOUT_FIELDS,
  INTAKE_MEDICARE_FIELDS,
  INTAKE_PROMO_FIELDS,
  FIELD_NAME,
  errorId,
  intakeFieldNames,
};
export type { IntakeErrorKey, IntakeErrors };
